import axios from 'axios';
import { wrapAxiosInstance } from '../cache/modules/axiosFactory';
import { setupAuthInterceptor } from '../common';
import { NAMESPACES } from '../configuration';
const BASE_URL = NAMESPACES.management_api.sales_statistics;


// Helper function to configure the Axios instance
const api = setupAuthInterceptor(axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
}));

// Cached API wrapper for this namespace/group
const cachedApi = wrapAxiosInstance(api, {
  namespace: 'management_api',
  group: 'sales_statistics',
  defaultTtl: '30s',
  hashLargeSegments: true,
  emitMeta: true,
});

// /GetRealTimeSalesStatisticsByLocalId/<int:local_id>

export const getRealTimeSalesStatisticsByLocalId = async (localId: number) => {
  return await api.get(`/GetRealTimeSalesStatisticsByLocalId/${localId}`);
    }

// /GetHistoricalSalesStatisticsByLocalId/<int:local_id>

export const getHistoricalSalesStatisticsByLocalId = async (localId: number) => {
  return await cachedApi.get(`/GetHistoricalSalesStatisticsByLocalId/${localId}`);
}

// /GetSessionStatisticsBySessionId/<int:session_id>

export const getSessionStatisticsBySessionId = async (sessionId: number) => {
  return await cachedApi.get(`/GetSessionStatisticsBySessionId/${sessionId}`);
}

/**
 * 
frequency_parser = reqparse.RequestParser()
frequency_parser.add_argument('start_date', type=str, required=False, help='Start date for analysis (ISO format, optional)')
frequency_parser.add_argument('end_date', type=str, required=False, help='End date for analysis (ISO format, optional)')

@ns.route('/GetFrequencyStatisticsByLocalId/<int:local_id>')
class GetFrequencyStatisticsByLocalId(Resource):
    @ns.doc('Get receipt frequency statistics by day and hour')
    @ns.expect(frequency_parser)
    @jwt_required()
    def get(self, local_id):
        """
        Get receipt frequency statistics across days of the week and hours of the day.
        
        This endpoint analyzes receipt patterns to show:
        - How many receipts occur in each hour (0-1, 1-2, ..., 23-24) for each day of the week
        - Total revenue generated in each day-hour bucket
        - Average time from table seating (time_started) to payment (local_timestamp)
        - Rankings by frequency and revenue
        
        By default analyzes the last 90 sessions (~90 days). Use start_date/end_date to customize range.
        
        Performance optimized:
        - Uses session-based filtering (indexed)
        - Queries only 4 fields per receipt
        - Processes data in-memory for speed
        
        Query Parameters:
        - start_date: Optional. ISO date string (e.g., "2025-01-01"). Default: 90 days ago
        - end_date: Optional. ISO date string (e.g., "2025-03-31"). Default: today
        
        Returns:
        {
          "frequency": {
            "Monday": {
              "averageTimeToPayMinutes": 45.5,
              "buckets": {
                "0-1": {"count": 5, "totalAmount": 250.00},
                "1-2": {"count": 3, "totalAmount": 150.00},
                ...
              }
            },
            ...
          },
          "overallAverageTimeToPayMinutes": 42.3,
          "rankings": {
            "byFrequency": [{day, hourRange, count, totalAmount}, ...],
            "byTotalAmount": [{day, hourRange, count, totalAmount}, ...]
          },
          "dateRange": {
            "start": "2025-01-01T00:00:00",
            "end": "2025-03-31T23:59:59"
          },
          "metadata": {
            "totalReceipts": 1500,
            "totalSessions": 90,
            "receiptsWithTimeTracking": 1200
          }
        }
        """
        overall_start = time.time()
        logger.info("⏱️ [ENDPOINT PERF] ═══════════════════════════════════════════════════════")
        logger.info(f"⏱️ [ENDPOINT PERF] Starting GetFrequencyStatisticsByLocalId for local_id={local_id}")
        
        args = frequency_parser.parse_args()
        start_date = args.get('start_date')
        end_date = args.get('end_date')
        
        helper = SalesStatisticsHelper(local_id=local_id)
        
        # Get frequency statistics
        stats_start = time.time()
        success, stats, status = helper.getFrequencyStatistics(
            local_id=local_id,
            start_date=start_date,
            end_date=end_date
        )
        stats_time = time.time() - stats_start
        
        if not success:
            logger.error(f"⏱️ [ENDPOINT PERF] Failed to get frequency statistics after {stats_time:.3f}s")
            return stats, status
        
        overall_time = time.time() - overall_start
        logger.info("⏱️ [ENDPOINT PERF] ─────────────────────────────────────────────────────")
        logger.info(f"⏱️ [ENDPOINT PERF] TOTAL ENDPOINT TIME: {overall_time:.3f} seconds")
        logger.info(f"⏱️ [ENDPOINT PERF] Processed {stats.get('metadata', {}).get('totalReceipts', 0)} receipts")
        logger.info("⏱️ [ENDPOINT PERF] ═══════════════════════════════════════════════════════")
        
        return stats, status  
 */

export const getFrequencyStatisticsByLocalId = async (localId: number, startDate?: string, endDate?: string) => {
  const params: any = {};
  if (startDate) params.start_date = startDate;
  if (endDate) params.end_date = endDate;

  return await cachedApi.get(`/GetFrequencyStatisticsByLocalId/${localId}`, { params });
}